import { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { CheckCircle, XCircle, Clock, Eye, Filter, X, Search, User, Calendar, ChevronDown, ChevronUp, Pencil, Loader2, Maximize2, Minimize2, Trash2 } from 'lucide-react';
import { api } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { SelectSingle } from './SelectSingle';
import { enqueueSnackbar } from 'notistack';
import { stripQuillColors } from '../lib/utils';
import { InputText } from './InputText';
import { TextArea } from './TextArea';
import { MilitarSelectField } from './MilitarSelectField';
import { PatenteSelectField } from './PatenteSelectField';
import { OficiaisSelectField } from './OficiaisSelectField';
import { PracasSelectField } from './PracasSelectField';
import { RichTextEditor } from './RichTextEditor';
import { DatePickerField } from './DatePickerField';
import { MilitaresMultiSelectField } from './MilitaresMultiSelectField';

interface GrupoAcaoCampo {
  campo_id: string;
  id_grupo_tipo_acao_campo: string;
  valor: string;
  label: string;
  tipo_campo: string;
  tipo_input: string;
  militar_nick?: string;
  patente_nome?: string;
  treinamento_nome?: string;
  opcoes?: Array<{ value: string; label: string }>;
  placeholder?: string;
  validacao?: { required?: boolean; min?: number; max?: number; minLength?: number; maxLength?: number; step?: number };
}

interface GrupoAcao {
  id: string;
  criado_em: string;
  militar: {
    id: string;
    nick: string;
    patente: {
      patente: string;
    };
  };
  gruposTiposAcoes: {
    descricao: string;
  };
  campos?: GrupoAcaoCampo[];
}

interface GrupoAcaoAprovacao {
  id: string;
  status: 'pendente' | 'aprovado' | 'rejeitado';
  observacoes?: string;
  aprovado_em?: string;
  rejeitado_em?: string;
  criado_em: string;
  militar_aprovador?: {
    nick: string;
    patente: {
      patente: string;
    };
  };
  acao: GrupoAcao;
}

interface Militar {
  id: string;
  nick: string;
  patente: {
    patente: string;
  };
}

interface GrupoAcoesAprovacaoListProps {
  grupoId: string;
  canApprove?: boolean;
  filterByCurrentUser?: boolean;
  dbrEnabled?: boolean;
}

export function GrupoAcoesAprovacaoList({ grupoId, canApprove = false, filterByCurrentUser = false, dbrEnabled = false }: GrupoAcoesAprovacaoListProps) {
  const PAGE_SIZE = 30;
  const [acoes, setAcoes] = useState<GrupoAcaoAprovacao[]>([]);
  const [totalAcoes, setTotalAcoes] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [filter, setFilter] = useState<'todas' | 'pendente' | 'aprovado' | 'rejeitado'>('todas');
  const [selectedAction, setSelectedAction] = useState<GrupoAcaoAprovacao | null>(null);
  const [observacoes, setObservacoes] = useState('');
  const [approving, setApproving] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  // Controla quais campos richtext estão expandidos na listagem (chave: "aprovacaoId-index")
  const [expandedRichtext, setExpandedRichtext] = useState<Set<string>>(new Set());
  const [isDetailExpanded, setIsDetailExpanded] = useState(false);

  // Estados para modal de exclusão lógica
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [actionToDelete, setActionToDelete] = useState<GrupoAcaoAprovacao | null>(null);
  const [deleting, setDeleting] = useState(false);

  const toggleRichtext = (key: string) => {
    setExpandedRichtext(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  // Estados para modal de rejeição
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [actionToReject, setActionToReject] = useState<GrupoAcaoAprovacao | null>(null);
  const [rejectObservacoes, setRejectObservacoes] = useState('');

  // Estados para modal de edição de ação (DBR)
  const [editingAction, setEditingAction] = useState<GrupoAcaoAprovacao | null>(null);
  const [editFormData, setEditFormData] = useState<Record<string, string>>({});
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  // Filtros avançados
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');
  const [militarSelecionado, setMilitarSelecionado] = useState('');
  const [militares, setMilitares] = useState<Militar[]>([]);
  const [loadingMilitares, setLoadingMilitares] = useState(false);
  const [militarSearchTerm, setMilitarSearchTerm] = useState('');
  const [showMilitarDropdown, setShowMilitarDropdown] = useState(false);

  const { militar } = useAuth();
  const militarDropdownRef = useRef<HTMLDivElement>(null);

  // Fechar dropdown quando clicar fora
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (militarDropdownRef.current && !militarDropdownRef.current.contains(event.target as Node)) {
        setShowMilitarDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Função para gerar resumo dos campos preenchidos
  const gerarResumo = (campos: Array<{
    valor: string;
    label: string;
    tipo_campo: string;
    tipo_input: string;
    militar_nick?: string;
    patente_nome?: string;
    treinamento_nome?: string;
  }> = []): string => {
    if (!campos || campos.length === 0) {
      return 'Nenhum campo preenchido';
    }

    const resumo = campos
      .filter(campo => campo.valor && campo.valor.trim() !== '')
      .map(campo => {
        let valorFormatado = campo.valor;

        // Formatação específica por tipo de campo
        switch (campo.tipo_input) {
          case 'date':
            try {
              const data = new Date(campo.valor);
              valorFormatado = format(data, 'dd/MM/yyyy', { locale: ptBR });
            } catch {
              valorFormatado = campo.valor;
            }
            break;
          case 'datetime-local':
            try {
              const data = new Date(campo.valor);
              valorFormatado = format(data, 'dd/MM/yyyy HH:mm', { locale: ptBR });
            } catch {
              valorFormatado = campo.valor;
            }
            break;
          case 'militar-select':
          case 'pracas-select':
          case 'oficiais-select':
            valorFormatado = campo.militar_nick || campo.valor;
            break;
          case 'patente-select':
            valorFormatado = campo.patente_nome || campo.valor;
            break;
          case 'treinamento-select':
            valorFormatado = campo.treinamento_nome || campo.valor;
            break;
          case 'militares-multiselect':
            try {
              const lista = JSON.parse(campo.valor);
              if (Array.isArray(lista)) {
                valorFormatado = lista.map((m: { nick: string }) => m.nick).join(', ');
              }
            } catch {
              valorFormatado = campo.valor;
            }
            break;
          case 'richtext':
            // Remover tags HTML para exibição resumida
            valorFormatado = campo.valor.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
            if (valorFormatado.length > 50) {
              valorFormatado = valorFormatado.substring(0, 47) + '...';
            }
            break;
          default:
            // Limitar o tamanho do texto para campos longos
            if (valorFormatado.length > 50) {
              valorFormatado = valorFormatado.substring(0, 47) + '...';
            }
            break;
        }

        return `${campo.label}: ${valorFormatado}`;
      });

    if (resumo.length === 0) {
      return 'Campos sem valores preenchidos';
    }

    return resumo.slice(0, 3).join(' • ') + (resumo.length > 3 ? ` (+${resumo.length - 3} mais)` : '');
  };

  const loadAcoes = async (page = currentPage, silent = false) => {
    try {
      if (silent) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }
      const params: any = {
        status: filter === 'todas' ? undefined : filter,
        includeApprovalInfo: true,
        page,
        size: PAGE_SIZE,
      };

      if (dataInicio) {
        params.dataInicio = dataInicio;
      }
      if (dataFim) {
        params.dataFim = dataFim;
      }

      // Se filterByCurrentUser estiver ativo, filtrar apenas ações do usuário logado
      if (filterByCurrentUser && militar?.id) {
        params.id_militar = militar.id;
      } else if (militarSelecionado) {
        params.id_militar = militarSelecionado;
      }

      const response = await api.get(`/grupos/${grupoId}/acoes`, { params });

      const acoesData = response.data.data || response.data;

      setAcoes(acoesData);
      setTotalAcoes(response.data.total ?? acoesData.length);
      setTotalPages(response.data.totalPages ?? 1);
      setCurrentPage(response.data.currentPage ?? page);
    } catch (error) {
      console.error('Erro ao carregar ações:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const loadMilitares = async () => {
    try {
      setLoadingMilitares(true);
      const response = await api.get(`/grupos/${grupoId}/militares`);
      // A API retorna { data: militares }
      const data = response.data.data || response.data;
      setMilitares(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar militares:', error);
      setMilitares([]); // Em caso de erro, definir como array vazio
    } finally {
      setLoadingMilitares(false);
    }
  };

  const handleApproveAction = async (aprovacao: GrupoAcaoAprovacao) => {
    try {
      setApproving(true);
      await api.patch(`/grupos/acoes/aprovacoes/${aprovacao.id}`, {
        status: 'aprovado'
      });

      enqueueSnackbar('Ação aprovada com sucesso!', { variant: 'success' });
      await loadAcoes(currentPage, true);
    } catch (error: any) {
      console.error('Erro ao aprovar ação:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao aprovar ação',
        { variant: 'error' }
      );
    } finally {
      setApproving(false);
    }
  };

  const handleRejectAction = (aprovacao: GrupoAcaoAprovacao) => {
    setActionToReject(aprovacao);
    setRejectObservacoes('');
    setShowRejectModal(true);
  };

  const handleDeleteAction = (aprovacao: GrupoAcaoAprovacao) => {
    setActionToDelete(aprovacao);
    setShowDeleteModal(true);
  };

  const confirmDeleteAction = async () => {
    if (!actionToDelete) return;
    try {
      setDeleting(true);
      await api.delete(`/grupos/acoes/${actionToDelete.acao.id}`);
      setShowDeleteModal(false);
      setActionToDelete(null);
      enqueueSnackbar('Ação excluída com sucesso!', { variant: 'success' });
      await loadAcoes(currentPage, true);
    } catch (error: any) {
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao excluir ação',
        { variant: 'error' }
      );
    } finally {
      setDeleting(false);
    }
  };

  const confirmRejectAction = async () => {
    if (!actionToReject || !rejectObservacoes.trim()) {
      enqueueSnackbar('As observações são obrigatórias para rejeitar uma ação', { variant: 'error' });
      return;
    }

    try {
      setApproving(true);
      await api.patch(`/grupos/acoes/aprovacoes/${actionToReject.id}`, {
        status: 'rejeitado',
        observacoes: rejectObservacoes.trim()
      });

      setShowRejectModal(false);
      setActionToReject(null);
      setRejectObservacoes('');
      enqueueSnackbar('Ação rejeitada com sucesso!', { variant: 'success' });
      await loadAcoes(currentPage, true);
    } catch (error: any) {
      console.error('Erro ao rejeitar ação:', error);
      enqueueSnackbar(
        error.response?.data?.message || 'Erro ao rejeitar ação',
        { variant: 'error' }
      );
    } finally {
      setApproving(false);
    }
  };

  const clearFilters = () => {
    setDataInicio('');
    setDataFim('');
    setMilitarSelecionado('');
    setMilitarSearchTerm('');
    setShowMilitarDropdown(false);
    setCurrentPage(1);
  };

  const handleOpenEdit = (aprovacao: GrupoAcaoAprovacao) => {
    const initialData: Record<string, string> = {};
    aprovacao.acao.campos?.forEach(campo => {
      initialData[campo.campo_id] = campo.valor || '';
    });
    setEditFormData(initialData);
    setEditingAction(aprovacao);
  };

  const handleSaveEdit = async () => {
    if (!editingAction || isSubmittingEdit) return;
    setIsSubmittingEdit(true);
    try {
      const valores_campos = editingAction.acao.campos?.map(campo => ({
        id_grupo_acao_campo: campo.campo_id,
        valor: editFormData[campo.campo_id] || '',
      })) || [];

      await api.put(`/grupos/acoes/${editingAction.acao.id}`, { valores_campos });
      enqueueSnackbar('Ação atualizada com sucesso!', { variant: 'success' });
      setEditingAction(null);
      await loadAcoes(currentPage, true);
    } catch (error: any) {
      enqueueSnackbar(error.response?.data?.message || 'Erro ao atualizar ação', { variant: 'error' });
    } finally {
      setIsSubmittingEdit(false);
    }
  };

  const renderEditCampo = (campo: GrupoAcaoCampo) => {
    const value = editFormData[campo.campo_id] || '';
    const onChange = (val: string) => setEditFormData(prev => ({ ...prev, [campo.campo_id]: val }));

    switch (campo.tipo_input) {
      case 'text':
      case 'email':
      case 'url':
      case 'tel':
        return (
          <InputText title="" name={campo.campo_id} value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={campo.placeholder || `Digite ${campo.label.toLowerCase()}...`}
            required={campo.validacao?.required} type={campo.tipo_input} />
        );
      case 'number':
        return (
          <InputText title="" name={campo.campo_id} value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={campo.placeholder || ''} required={campo.validacao?.required}
            type="number" min={campo.validacao?.min} max={campo.validacao?.max}
            step={campo.validacao?.step ?? 1} />
        );
      case 'date':
        return (
          <DatePickerField
            value={value}
            onChange={onChange}
            required={campo.validacao?.required}
          />
        );
      case 'datetime-local':
        return (
          <input type="datetime-local" value={value} onChange={e => onChange(e.target.value)}
            required={campo.validacao?.required}
            className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
        );
      case 'textarea':
        return (
          <TextArea title="" name={campo.campo_id} value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={campo.placeholder || `Digite ${campo.label.toLowerCase()}...`}
            required={campo.validacao?.required} />
        );
      case 'select':
      case 'multiselect':
        return (
          <SelectSingle title="" name={campo.campo_id} value={value}
            onChange={e => onChange(e.target.value)} required={campo.validacao?.required}>
            <option value="">Selecione uma opção...</option>
            {campo.opcoes?.map(op => (
              <option key={op.value} value={op.value}>{op.label}</option>
            ))}
          </SelectSingle>
        );
      case 'militar-select':
        return (
          <MilitarSelectField value={value} onChange={id => onChange(id)}
            placeholder={campo.placeholder || `Selecione ${campo.label.toLowerCase()}...`}
            required={campo.validacao?.required} />
        );
      case 'patente-select':
        return (
          <PatenteSelectField value={value} onChange={id => onChange(id)}
            placeholder={campo.placeholder || `Selecione ${campo.label.toLowerCase()}...`}
            required={campo.validacao?.required} />
        );
      case 'oficiais-select':
        return (
          <OficiaisSelectField value={value} onChange={id => onChange(id)}
            placeholder={campo.placeholder || `Selecione ${campo.label.toLowerCase()}...`}
            required={campo.validacao?.required} />
        );
      case 'pracas-select':
        return (
          <PracasSelectField value={value} onChange={id => onChange(id)}
            placeholder={campo.placeholder || `Selecione ${campo.label.toLowerCase()}...`}
            required={campo.validacao?.required} />
        );
      case 'militares-multiselect':
        return (
          <MilitaresMultiSelectField
            value={value}
            onChange={onChange}
            placeholder={campo.placeholder || 'Pesquise e selecione militares...'}
            required={campo.validacao?.required}
          />
        );
      case 'richtext':
        return (
          <RichTextEditor value={value} onChange={onChange} enableImages />
        );
      default:
        return (
          <InputText title="" name={campo.campo_id} value={value}
            onChange={e => onChange(e.target.value)}
            placeholder={campo.placeholder || ''} required={campo.validacao?.required} />
        );
    }
  };

  const hasActiveFilters = dataInicio || dataFim || militarSelecionado;

  useEffect(() => {
    loadAcoes(currentPage);
  }, [grupoId, filter, dataInicio, dataFim, militarSelecionado, filterByCurrentUser, militar?.id, currentPage]);

  useEffect(() => {
    if (grupoId && militares.length === 0) {
      loadMilitares();
    }
  }, [grupoId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aprovado':
        return 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300';
      case 'rejeitado':
        return 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
      case 'pendente':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300';
    }
  };

  const getStatusBorderColor = (status: string) => {
    switch (status) {
      case 'aprovado':
        return 'border-l-green-500';
      case 'rejeitado':
        return 'border-l-red-500';
      case 'pendente':
        return 'border-l-yellow-500';
      default:
        return 'border-l-gray-500';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'aprovado':
        return <CheckCircle className="h-4 w-4" />;
      case 'rejeitado':
        return <XCircle className="h-4 w-4" />;
      case 'pendente':
        return <Clock className="h-4 w-4" />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Ações do Grupo</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-2">Carregando ações...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <Card className="min-h-[700px]">
        <CardHeader>
          <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              Ações do Grupo
              <Badge variant="secondary">
                {totalAcoes} {totalAcoes === 1 ? 'ação' : 'ações'} {hasActiveFilters ? 'filtrada(s)' : 'encontrada(s)'}
              </Badge>
              {refreshing && (
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={filter === 'todas' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setFilter('todas'); setCurrentPage(1); }}
              >
                Todas
              </Button>
              <Button
                variant={filter === 'pendente' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setFilter('pendente'); setCurrentPage(1); }}
              >
                Pendentes
              </Button>
              <Button
                variant={filter === 'aprovado' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setFilter('aprovado'); setCurrentPage(1); }}
              >
                Aprovadas
              </Button>
              <Button
                variant={filter === 'rejeitado' ? 'default' : 'outline'}
                size="sm"
                onClick={() => { setFilter('rejeitado'); setCurrentPage(1); }}
              >
                Rejeitadas
              </Button>
            </div>
          </CardTitle>

          {/* Seção de Filtros */}
          <div className="mt-4 p-4 border-t border-border space-y-4">
            <div className="flex items-center justify-between mb-2">
              <h3 className="flex items-center gap-2 text-lg font-semibold text-foreground">
                <Calendar className="h-5 w-5 text-blue-600" />
                Filtros Avançados
              </h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-red-600 hover:text-red-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Limpar
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label htmlFor="dataInicio">Data Início</Label>
                <DatePickerField
                  id="dataInicio"
                  value={dataInicio}
                  onChange={(value) => setDataInicio(value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label htmlFor="dataFim">Data Fim</Label>
                <DatePickerField
                  id="dataFim"
                  value={dataFim}
                  onChange={(value) => setDataFim(value)}
                  className="mt-1"
                />
              </div>

              {!filterByCurrentUser && (
                <div className="relative" ref={militarDropdownRef}>
                  <Label htmlFor="militar">Militar</Label>
                  <div className="relative">
                    <input
                      id="militar"
                      type="text"
                      value={militarSearchTerm}
                      onChange={(e) => {
                        setMilitarSearchTerm(e.target.value);
                        setShowMilitarDropdown(true);
                      }}
                      onFocus={() => setShowMilitarDropdown(true)}
                      placeholder="Digite o nick do militar..."
                      className="w-full mt-1 px-3 py-2 pr-10 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent"
                      disabled={loadingMilitares}
                    />
                    <Search className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 mt-0.5" />

                  {showMilitarDropdown && (
                    <div className="dropdown-container absolute z-10 w-full mt-1 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setMilitarSelecionado('');
                          setMilitarSearchTerm('');
                          setShowMilitarDropdown(false);
                        }}
                        className="w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none border-b border-border"
                      >
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Todos os militares</span>
                        </div>
                      </button>

                      {Array.isArray(militares) && militares
                        .filter(militar =>
                          militar?.nick?.toLowerCase().includes(militarSearchTerm.toLowerCase())
                        )
                        .slice(0, 10)
                        .map(militar => (
                          <button
                            key={militar.id}
                            type="button"
                            onClick={() => {
                              setMilitarSelecionado(militar.id);
                              setMilitarSearchTerm(militar.nick || '');
                              setShowMilitarDropdown(false);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-muted focus:bg-muted focus:outline-none"
                          >
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-muted-foreground" />
                              <span>{militar.nick || 'N/A'}</span>
                            </div>
                          </button>
                        ))}

                      {Array.isArray(militares) && militares.filter(militar =>
                        militar?.nick?.toLowerCase().includes(militarSearchTerm.toLowerCase())
                      ).length > 10 && (
                        <div className="px-3 py-2 text-sm text-muted-foreground border-t border-border">
                          {militares.filter(militar =>
                            militar?.nick?.toLowerCase().includes(militarSearchTerm.toLowerCase())
                          ).length - 10} mais resultados...
                        </div>
                      )}

                      {Array.isArray(militares) && militares.filter(militar =>
                        militar?.nick?.toLowerCase().includes(militarSearchTerm.toLowerCase())
                      ).length === 0 && militarSearchTerm && (
                        <div className="dropdown-container p-3">
                          <div className="text-center text-muted-foreground">
                            <User className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
                            <p>Nenhum militar encontrado</p>
                            <p className="text-sm">Tente outro nick</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {acoes.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              Nenhuma ação encontrada.
            </div>
          ) : (
            <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
              {acoes.map((aprovacao) => (
                <Card key={aprovacao.id} className={`border-l-4 ${getStatusBorderColor(aprovacao.status)}`}>
                  <CardContent className="pt-4">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-2">
                          <Badge className={`flex items-center gap-1 ${getStatusColor(aprovacao.status)}`}>
                            {getStatusIcon(aprovacao.status)}
                            {aprovacao.status.charAt(0).toUpperCase() + aprovacao.status.slice(1)}
                          </Badge>
                          <span className="text-sm text-muted-foreground">
                            {format(new Date(aprovacao.criado_em), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                          </span>
                        </div>

                        <h4 className="font-semibold mb-1">{aprovacao.acao.gruposTiposAcoes.descricao}</h4>

                        <div className="text-sm text-muted-foreground mb-2">
                          <strong>Criado por:</strong> {aprovacao.acao.militar.patente.patente} {aprovacao.acao.militar.nick}
                        </div>

                        {/* Dados detalhados dos campos */}
                        {aprovacao.acao.campos && aprovacao.acao.campos.length > 0 ? (
                          <div className="hidden sm:block text-sm bg-muted/50 p-3 rounded mb-2">
                            <strong className="text-foreground mb-2 block">Dados da Ação:</strong>
                            {aprovacao.acao.campos.filter(campo => campo.valor && campo.valor.trim() !== '').length > 0 ? (
                              <div className="space-y-1">
                                {aprovacao.acao.campos
                                  .filter(campo => campo.valor && campo.valor.trim() !== '')
                                  .map((campo, index) => {
                                  let valorFormatado = campo.valor;

                                  // Formatação por tipo de campo
                                  switch (campo.tipo_input) {
                                    case 'date':
                                      try {
                                        const data = new Date(campo.valor);
                                        valorFormatado = format(data, 'dd/MM/yyyy', { locale: ptBR });
                                      } catch {
                                        valorFormatado = campo.valor;
                                      }
                                      break;
                                    case 'datetime-local':
                                      try {
                                        const data = new Date(campo.valor);
                                        valorFormatado = format(data, 'dd/MM/yyyy HH:mm', { locale: ptBR });
                                      } catch {
                                        valorFormatado = campo.valor;
                                      }
                                      break;
                                    case 'militar-select':
                                    case 'pracas-select':
                                    case 'oficiais-select':
                                      valorFormatado = campo.militar_nick || campo.valor;
                                      break;
                                    case 'patente-select':
                                      valorFormatado = campo.patente_nome || campo.valor;
                                      break;
                                    case 'treinamento-select':
                                      valorFormatado = campo.treinamento_nome || campo.valor;
                                      break;
                                    case 'militares-multiselect':
                                      try {
                                        const lista = JSON.parse(campo.valor);
                                        if (Array.isArray(lista)) {
                                          valorFormatado = lista.map((m: { nick: string }) => m.nick).join(', ');
                                        }
                                      } catch {
                                        valorFormatado = campo.valor;
                                      }
                                      break;
                                    default:
                                      // Limitar texto muito longo
                                      if (valorFormatado.length > 80) {
                                        valorFormatado = valorFormatado.substring(0, 77) + '...';
                                      }
                                      break;
                                  }

                                  if (campo.tipo_input === 'richtext') {
                                    const key = `${aprovacao.id}-${index}`;
                                    const expanded = expandedRichtext.has(key);
                                    const textoPlano = campo.valor.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
                                    return (
                                      <div key={index} className="py-1">
                                        <button
                                          type="button"
                                          onClick={() => toggleRichtext(key)}
                                          className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors w-full text-left"
                                        >
                                          {expanded ? <ChevronUp className="h-3 w-3 flex-shrink-0" /> : <ChevronDown className="h-3 w-3 flex-shrink-0" />}
                                          <span>{campo.label}</span>
                                          {!expanded && (
                                            <span className="text-xs text-muted-foreground/70 ml-1 truncate max-w-[200px]">
                                              — {textoPlano.substring(0, 60)}{textoPlano.length > 60 ? '...' : ''}
                                            </span>
                                          )}
                                        </button>
                                        {expanded && (
                                          <div
                                            className="ql-editor prose prose-sm max-w-none text-foreground border border-border rounded p-2 bg-background mt-1"
                                            dangerouslySetInnerHTML={{ __html: stripQuillColors(campo.valor) }}
                                          />
                                        )}
                                      </div>
                                    );
                                  }

                                  return (
                                    <div key={index} className="flex items-start py-1 gap-2">
                                      <span className="font-medium text-muted-foreground flex-shrink-0">
                                        {campo.label}:
                                      </span>
                                      <span className="text-foreground break-words">
                                        {valorFormatado}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            ) : (
                              <div className="text-muted-foreground italic">
                                Nenhum campo foi preenchido nesta ação
                              </div>
                            )}
                            {aprovacao.acao.campos.filter(c => !c.valor || c.valor.trim() === '').length > 0 && (
                              <div className="text-xs text-muted-foreground mt-2 pt-2 border-t border-border">
                                {aprovacao.acao.campos.filter(c => !c.valor || c.valor.trim() === '').length} campo(s) não preenchido(s)
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="hidden sm:block text-sm bg-muted/50 p-3 rounded mb-2">
                            <span className="text-muted-foreground italic">Esta ação não possui campos configurados</span>
                          </div>
                        )}

                        {aprovacao.militar_aprovador && (
                          <div className="hidden sm:block text-sm text-muted-foreground mb-2">
                            <strong>Processado por:</strong> {aprovacao.militar_aprovador.patente.patente} {aprovacao.militar_aprovador.nick}
                            {(aprovacao.aprovado_em || aprovacao.rejeitado_em) && (
                              <span className="ml-2">
                                em {format(new Date(aprovacao.aprovado_em || aprovacao.rejeitado_em!), 'dd/MM/yyyy HH:mm', { locale: ptBR })}
                              </span>
                            )}
                          </div>
                        )}

                        {aprovacao.observacoes && (
                          <div className="hidden sm:block text-sm bg-muted/50 p-2 rounded mt-2">
                            <strong>Observações:</strong> {aprovacao.observacoes}
                          </div>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 sm:ml-4 shrink-0">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setSelectedAction(aprovacao)}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>

                        {dbrEnabled && (canApprove || aprovacao.acao.militar.id === militar?.id) && (aprovacao.status === 'pendente' || aprovacao.status === 'aprovado') && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20"
                            onClick={() => handleOpenEdit(aprovacao)}
                            title="Editar ação"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}

                        {dbrEnabled && canApprove && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                            onClick={() => handleDeleteAction(aprovacao)}
                            title="Excluir ação"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}

                        {canApprove && aprovacao.status === 'pendente' && (
                          <>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-green-600 hover:text-green-700 hover:bg-green-50 dark:hover:bg-green-900/20"
                              onClick={() => handleApproveAction(aprovacao)}
                              disabled={approving}
                              title="Aprovar ação"
                            >
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/20"
                              onClick={() => handleRejectAction(aprovacao)}
                              disabled={approving}
                              title="Rejeitar ação"
                            >
                              <XCircle className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Paginação */}
          {totalPages > 1 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 border-t border-border">
              <span className="text-sm text-muted-foreground">
                Página {currentPage} de {totalPages} ({totalAcoes} {totalAcoes === 1 ? 'ação' : 'ações'})
              </span>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                >
                  Anterior
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const start = Math.max(1, Math.min(currentPage - 2, totalPages - 4));
                  const page = start + i;
                  return (
                    <Button
                      key={page}
                      variant={page === currentPage ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setCurrentPage(page)}
                      disabled={loading}
                      className="min-w-[2.25rem]"
                    >
                      {page}
                    </Button>
                  );
                })}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || loading}
                >
                  Próximo
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal para detalhes da ação */}
      {selectedAction && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isDetailExpanded ? 'p-0' : 'p-4'}`}>
          <Card className={`w-full flex flex-col ${isDetailExpanded ? 'h-full rounded-none' : 'max-w-2xl max-h-[90vh]'}`}>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <CardTitle>Detalhes da Ação</CardTitle>
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => setIsDetailExpanded(prev => !prev)}
                  title={isDetailExpanded ? 'Restaurar' : 'Expandir'}
                >
                  {isDetailExpanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0"
                  onClick={() => { setSelectedAction(null); setIsDetailExpanded(false); }}
                  title="Fechar"
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto flex-1">
              <div>
                <strong>Ação:</strong> {selectedAction.acao.gruposTiposAcoes.descricao}
              </div>
              <div>
                <strong>Criado por:</strong> {selectedAction.acao.militar.patente.patente} {selectedAction.acao.militar.nick}
              </div>
              <div>
                <strong>Status:</strong>
                <Badge className={`ml-2 ${getStatusColor(selectedAction.status)}`}>
                  {selectedAction.status.charAt(0).toUpperCase() + selectedAction.status.slice(1)}
                </Badge>
              </div>

              {/* Detalhes dos campos preenchidos */}
              {selectedAction.acao.campos && selectedAction.acao.campos.length > 0 && (
                <div>
                  <strong>Campos Preenchidos:</strong>
                  <div className="bg-muted/50 p-3 rounded mt-2 space-y-2">
                    {selectedAction.acao.campos
                      .filter(campo => campo.valor && campo.valor.trim() !== '')
                      .map((campo, index) => {
                        let valorFormatado = campo.valor;

                        // Formatação por tipo de campo
                        switch (campo.tipo_input) {
                          case 'date':
                            try {
                              const data = new Date(campo.valor);
                              valorFormatado = format(data, 'dd/MM/yyyy', { locale: ptBR });
                            } catch {
                              valorFormatado = campo.valor;
                            }
                            break;
                          case 'datetime-local':
                            try {
                              const data = new Date(campo.valor);
                              valorFormatado = format(data, 'dd/MM/yyyy HH:mm', { locale: ptBR });
                            } catch {
                              valorFormatado = campo.valor;
                            }
                            break;
                          case 'militar-select':
                            valorFormatado = campo.militar_nick || campo.valor;
                            break;
                          case 'patente-select':
                            valorFormatado = campo.patente_nome || campo.valor;
                            break;
                          case 'pracas-select':
                          case 'oficiais-select':
                            valorFormatado = campo.militar_nick || campo.valor;
                            break;
                          case 'treinamento-select':
                            valorFormatado = campo.treinamento_nome || campo.valor;
                            break;
                          case 'militares-multiselect':
                            try {
                              const lista = JSON.parse(campo.valor);
                              if (Array.isArray(lista)) {
                                valorFormatado = lista.map((m: { nick: string }) => m.nick).join(', ');
                              }
                            } catch {
                              valorFormatado = campo.valor;
                            }
                            break;
                        }

                        if (campo.tipo_input === 'richtext') {
                          return (
                            <div key={index} className="py-1">
                              <span className="font-medium text-muted-foreground block mb-1">{campo.label}:</span>
                              <div
                                className="ql-editor prose prose-sm max-w-none text-foreground border border-border rounded p-2 bg-muted/30"
                                dangerouslySetInnerHTML={{ __html: stripQuillColors(campo.valor) }}
                              />
                            </div>
                          );
                        }

                        return (
                          <div key={index} className="flex items-start gap-2">
                            <span className="font-medium text-muted-foreground flex-shrink-0">{campo.label}:</span>
                            <span className="text-foreground break-words">{valorFormatado}</span>
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {selectedAction.observacoes && (
                <div>
                  <strong>Observações:</strong>
                  <div className="bg-muted/50 p-2 rounded mt-1">
                    {selectedAction.observacoes}
                  </div>
                </div>
              )}

              <div className="flex justify-end">
                <Button
                  variant="outline"
                  onClick={() => { setSelectedAction(null); setIsDetailExpanded(false); }}
                >
                  Fechar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal para editar ação (DBR) */}
      {editingAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-2xl max-h-[90vh] flex flex-col">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Pencil className="h-5 w-5 text-[oklch(0.45_0.15_142)]" />
                  Editar Ação: {editingAction.acao.gruposTiposAcoes.descricao}
                </CardTitle>
                <CardDescription>
                  Atualize os campos necessários para editar esta ação
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setEditingAction(null)} className="h-6 w-6 p-0" disabled={isSubmittingEdit}>
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4 overflow-y-auto flex-1">
              {editingAction.acao.campos && editingAction.acao.campos.length > 0 ? (
                editingAction.acao.campos.map((campo) => (
                  <div key={campo.campo_id}>
                    <Label className="mb-1 block">{campo.label}{campo.validacao?.required && <span className="text-destructive ml-1">*</span>}</Label>
                    {renderEditCampo(campo)}
                  </div>
                ))
              ) : (
                <p className="text-muted-foreground italic text-sm">Esta ação não possui campos editáveis.</p>
              )}
              <div className="flex gap-2 pt-4">
                <Button variant="outline" onClick={() => setEditingAction(null)} className="flex-1" disabled={isSubmittingEdit}>
                  Cancelar
                </Button>
                <Button onClick={handleSaveEdit} disabled={isSubmittingEdit} className="flex-1 bg-[oklch(0.45_0.15_142)] hover:bg-[oklch(0.40_0.15_142)] text-white">
                  {isSubmittingEdit ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar Alterações'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal para rejeitar ação */}
      {showRejectModal && actionToReject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <XCircle className="h-5 w-5 text-red-600" />
                  Rejeitar Ação
                </CardTitle>
                <CardDescription>
                  Informe o motivo da rejeição desta ação
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setShowRejectModal(false);
                  setActionToReject(null);
                  setRejectObservacoes('');
                }}
                className="h-6 w-6 p-0"
                disabled={approving}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <strong>Ação:</strong> {actionToReject.acao.gruposTiposAcoes.descricao}
              </div>
              <div>
                <strong>Criado por:</strong> {actionToReject.acao.militar.patente.patente} {actionToReject.acao.militar.nick}
              </div>

              <div>
                <Label htmlFor="observacoes">Motivo da Rejeição *</Label>
                <textarea
                  id="observacoes"
                  value={rejectObservacoes}
                  onChange={(e) => setRejectObservacoes(e.target.value)}
                  placeholder="Descreva o motivo da rejeição desta ação..."
                  className="w-full mt-1 px-3 py-2 border border-border rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
                  rows={4}
                  disabled={approving}
                />
              </div>

              <div className="flex gap-2 pt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setShowRejectModal(false);
                    setActionToReject(null);
                    setRejectObservacoes('');
                  }}
                  className="flex-1"
                  disabled={approving}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmRejectAction}
                  disabled={approving || !rejectObservacoes.trim()}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {approving ? 'Rejeitando...' : 'Rejeitar Ação'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Modal para excluir ação */}
      {showDeleteModal && actionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Trash2 className="h-5 w-5 text-red-600" />
                  Excluir Ação
                </CardTitle>
                <CardDescription>
                  Esta ação não poderá ser desfeita
                </CardDescription>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => { setShowDeleteModal(false); setActionToDelete(null); }}
                className="h-6 w-6 p-0"
                disabled={deleting}
              >
                <X className="h-4 w-4" />
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Tem certeza que deseja excluir a ação abaixo? Ela deixará de aparecer na listagem e na página de notícias.
              </p>
              <div className="bg-muted/50 p-3 rounded text-sm space-y-1">
                <div><strong>Ação:</strong> {actionToDelete.acao.gruposTiposAcoes.descricao}</div>
                <div><strong>Criado por:</strong> {actionToDelete.acao.militar.patente.patente} {actionToDelete.acao.militar.nick}</div>
              </div>
              <div className="flex gap-2 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => { setShowDeleteModal(false); setActionToDelete(null); }}
                  className="flex-1"
                  disabled={deleting}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={confirmDeleteAction}
                  disabled={deleting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                >
                  {deleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Excluindo...
                    </>
                  ) : (
                    'Excluir Ação'
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
